import { graphql, useFragment } from 'react-relay';
import type { FC } from 'react';
import React, { useState, useCallback, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Spinner from '@atlaskit/spinner/spinner';
import PagesIcon from '@atlaskit/icon/core/pages';
import TasksIcon from '@atlaskit/icon/core/tasks';
import {
	ExpandableMenuItem,
	ExpandableMenuItemTrigger,
	ExpandableMenuItemContent,
} from '@atlaskit/side-nav-items/expandable-menu-item';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { PageTree } from '@confluence/page-tree/entry-points/PageTreeLoader.tsx';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { usePageSpaceKey, usePageContentId, useContentType } from '@confluence/page-context';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useHoverPageCard } from '@confluence/page-card/entry-points/useHoverPageCard.ts';
import { useIsSuperAdmin } from '@confluence/super-admin/entry-points/AdminKeyQuery.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { expVal } from '@confluence/feature-experiments';
import { useSpaceManagementOperations } from '@confluence/space-utils/entry-points/graphql.ts';
import { useSpacePersistentExpansion } from '@confluence/collapsible-section/entry-points/useSpacePersistentExpansion.ts';
import { SpaceHoverPageCard } from '@confluence/page-card/entry-points/SpaceHoverPageCard.ts';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import {
	SpaceViewsOptionsPersistenceFragment,
	useSpaceViewsOptions,
} from './useSpaceViewsOptions.tsx';
import { FlexCenter } from './PresentationalComponents.tsx';
import { useSpaceViewsExperienceTracking } from './useSpaceViewsExperienceTracking.ts';
import type {
	SpaceViewsControllerQuery,
	SpaceViewsControllerQueryVariables,
} from './__types__/SpaceViewsControllerQuery.ts';
import type { useSpaceViewsData_space$key } from './__generated__/useSpaceViewsData_space.graphql.ts';
import type {
	useSpaceViewsOptionsPersistence_userPreferences$data,
	useSpaceViewsOptionsPersistence_userPreferences$key,
} from './__generated__/useSpaceViewsOptionsPersistence_userPreferences.graphql.ts';

const SpaceViews = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SpaceViews" */ './SpaceViews.tsx')).SpaceViews,
});

const PagesItemActions = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-PagesItemActions" */ './PagesItemActions.tsx'))
			.PagesItemActions,
});

const ContentTreeSearchLoader = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-ContentTreeSearch" */ './ContentTreeSearch.tsx'))
			.ContentTreeSearch,
});

const i18n = defineMessages({
	contentTitle: {
		id: 'space-views.collapsible-section.title',
		defaultMessage: 'Content',
		description:
			'The text is shown as a collapsible section heading in the space sidebar that groups all content items (e.g. pages, blogs, whiteboards) belonging to the space.',
	},
	multiSelectContentTitle: {
		id: 'space-views.collapsible-section.title.multi-select',
		defaultMessage: 'Multi-select content',
		description: 'Header for all content section when multi-select is active',
	},
});

export type SpaceViewsRootFragmentKey = useSpaceViewsData_space$key;
export type SpaceViewsPersistenceFragmentKey = useSpaceViewsOptionsPersistence_userPreferences$key;

export type SpaceViewsControllerProps = {
	spaceViewsRootFragmentRef?: SpaceViewsRootFragmentKey;
	spaceViewsPersistenceFragmentRef?: SpaceViewsPersistenceFragmentKey;
	onDragDropSuccess?: (contentId: any) => void;
	isPeekingFromBlogs?: boolean;
	spaceHasUserContent: boolean;
	homepageId?: string;
	onContentTreeLoadComplete?: () => void;
	pageTreeFinishedLoading: boolean;
};

type SpaceViewsControllerComponentProps = SpaceViewsControllerProps & {
	initialPersistenceData?: useSpaceViewsOptionsPersistence_userPreferences$data;
};

const SpaceViewsControllerComponent: FC<SpaceViewsControllerComponentProps> = ({
	spaceViewsRootFragmentRef,
	initialPersistenceData,
	onDragDropSuccess,
	isPeekingFromBlogs,
	spaceHasUserContent,
	homepageId,
	onContentTreeLoadComplete,
	pageTreeFinishedLoading,
}) => {
	const { formatMessage } = useIntl();
	const { isSuperAdmin } = useIsSuperAdmin();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [pageContextContentType] = useContentType();
	const { isLicensed, edition } = useSessionData();

	const {
		spaceViewsSort,
		setSpaceViewsSort,
		queryLoading: persistenceLoading,
		queryError: persistenceError,
		mutationLoading: persistenceUpdateLoading,
		mutationError: persistenceUpdateError,
	} = useSpaceViewsOptions(
		// @ts-ignore FIXME: `spaceKey` can be `undefined` here, and needs proper handling
		spaceKey,
		isLicensed,
		initialPersistenceData,
	);
	const forceExpandBecauseViewingContentTree = pageContextContentType !== 'blogpost';

	const [isPageTreeLoading, setIsPageTreeLoading] = useState<boolean>(true);
	const [pageTreeError, setPageTreeError] = useState<Error | undefined>();
	const [isSpaceViewsLoading, setIsSpaceViewsLoading] = useState<boolean>(true);
	const [contentTreeSize, setContentTreeSize] = useState<number>(0);
	const [isPageTreeEmpty, setIsPageTreeEmpty] = useState<boolean>(false);
	const [spaceViewsError, setSpaceViewsError] = useState<ApolloErrorInterop | undefined>();
	const isPageTree = spaceViewsSort === 'TREE';
	const [isMultiSelectActive] = useIsMultiSelectActive();

	// Ref to the space views menu trigger button in the content header
	const spaceViewsMenuTriggerRef = useRef<HTMLButtonElement>(null);

	const {
		data,
		loading: isSpaceAdminQueryLoading,
		error: isSpaceAdminQueryError,
	} = useQueryInterop<SpaceViewsControllerQuery, SpaceViewsControllerQueryVariables>(
		graphql`
			query SpaceViewsControllerQuery($spaceKey: String!) {
				space(key: $spaceKey) {
					id
					currentUser {
						isAdmin
					}
				}
			}
		`,
		{
			variables: {
				spaceKey: spaceKey ?? '',
			},
			skip: expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false),
		},
	);

	const { canManageContent: canManageContentOp, loading: operationsLoading } =
		useSpaceManagementOperations({
			spaceKey: spaceKey ?? '',
			skip: !spaceKey || !expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false),
		});

	const loading =
		persistenceLoading ||
		(isPageTree ? isPageTreeLoading : isSpaceViewsLoading) ||
		(isSpaceAdminQueryLoading &&
			!expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false)) ||
		(operationsLoading && expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false));
	const error = pageTreeError || spaceViewsError || isSpaceAdminQueryError;

	const canManageContent = expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false)
		? Boolean(canManageContentOp)
		: Boolean(data?.space?.currentUser?.isAdmin);
	const canManageContentOrSuperAdmin = canManageContent || isSuperAdmin;

	const { trackedSetSpaceViewsSort, makeTrackedFetchMore } = useSpaceViewsExperienceTracking({
		loading,
		error,
		persistenceError,
		persistenceUpdateLoading,
		persistenceUpdateError,
		spaceViewsSort,
		setSpaceViewsSort,
		isSpaceAdmin: canManageContent,
	});

	const {
		onEnterHoverTarget: showHoverPageCardOnEnterHoverTarget,
		closeAllHoverPageCards,
		startCloseHoverPageCardTimer,
		clearHoverPageCardTimers,
		showHoverPageCardForItem,
		isHoverPageCardOptedIn,
		onLeaveHoverTarget,
		onBlurHoverTarget,
	} = useHoverPageCard(spaceViewsSort);

	const [shouldRenderAfterIcon, setShouldRenderAfterIcon] = useState(false);
	const onEnterHoverTarget = useCallback(
		({
			itemID,
			isQuickActionsFocused,
			hoverPageCardTriggerRef,
			isContextualCreateFocused,
		}: {
			itemID: string;
			isQuickActionsFocused: boolean;
			hoverPageCardTriggerRef: React.RefObject<HTMLDivElement>;
			isContextualCreateFocused?: boolean;
		}) => {
			showHoverPageCardOnEnterHoverTarget({
				itemID,
				isQuickActionsFocused,
				hoverPageCardTriggerRef,
				isContextualCreateFocused,
			});
			setShouldRenderAfterIcon(true);
		},
		[showHoverPageCardOnEnterHoverTarget, setShouldRenderAfterIcon],
	);

	const { isExpanded, onExpansionToggle } = useSpacePersistentExpansion({
		sectionName: 'contentTree',
		collapsedByDefault: !forceExpandBecauseViewingContentTree,
		forceExpand: forceExpandBecauseViewingContentTree,
	});

	const handleSpaceViewCleared = useCallback(() => {
		// Focus the space views menu trigger button in the content header using React ref
		if (spaceViewsMenuTriggerRef.current) {
			spaceViewsMenuTriggerRef.current.focus();
		}
	}, [spaceViewsMenuTriggerRef]);

	const renderContentTree = useMemo(() => {
		if (persistenceLoading) {
			return (
				<FlexCenter>
					<Spinner size="medium" />
				</FlexCenter>
			);
		}

		const hoverPageCardProps = {
			onEnterHoverTarget,
			closeAllHoverPageCards,
			isHoverPageCardOptedIn,
			onLeaveHoverTarget,
			onBlurHoverTarget,
		};

		if (isPageTree) {
			return (
				<ContentTreeSearchLoader
					contentTreeSize={contentTreeSize}
					hideSearchRow={isPageTreeEmpty && spaceHasUserContent}
					shouldRenderAfterIcon={shouldRenderAfterIcon}
					selection={spaceViewsSort}
					setSelection={trackedSetSpaceViewsSort}
					isLicensed={isLicensed}
					canManageContentOrSuperAdmin={canManageContentOrSuperAdmin}
					{...hoverPageCardProps}
				>
					<PageTree
						onDragDropSuccess={onDragDropSuccess}
						isPeekingFromBlogs={isPeekingFromBlogs}
						isSuperAdmin={isSuperAdmin}
						setIsPageTreeLoading={setIsPageTreeLoading}
						setPageTreeError={setPageTreeError}
						shouldRenderAfterIcon={shouldRenderAfterIcon}
						homepageId={homepageId}
						setContentTreeSize={setContentTreeSize}
						setIsPageTreeEmpty={setIsPageTreeEmpty}
						onContentTreeLoadComplete={onContentTreeLoadComplete}
						{...hoverPageCardProps}
					/>
				</ContentTreeSearchLoader>
			);
		}
		return (
			<SpaceViews
				spaceRootFragmentRef={spaceViewsRootFragmentRef}
				spaceViewsSort={spaceViewsSort}
				setIsSpaceViewsLoading={setIsSpaceViewsLoading}
				setSpaceViewsError={setSpaceViewsError}
				trackedSetSpaceViewsSort={trackedSetSpaceViewsSort}
				makeTrackedFetchMore={makeTrackedFetchMore}
				spaceKey={spaceKey}
				contentId={contentId}
				shouldRenderAfterIcon={shouldRenderAfterIcon}
				contentTreeSize={contentTreeSize}
				onContentTreeLoadComplete={onContentTreeLoadComplete}
				onSpaceViewCleared={handleSpaceViewCleared}
				{...hoverPageCardProps}
			/>
		);
	}, [
		persistenceLoading,
		spaceViewsRootFragmentRef,
		onEnterHoverTarget,
		closeAllHoverPageCards,
		isHoverPageCardOptedIn,
		onLeaveHoverTarget,
		onBlurHoverTarget,
		isPageTree,
		spaceViewsSort,
		trackedSetSpaceViewsSort,
		makeTrackedFetchMore,
		spaceKey,
		contentId,
		shouldRenderAfterIcon,
		contentTreeSize,
		isPageTreeEmpty,
		spaceHasUserContent,
		onContentTreeLoadComplete,
		isLicensed,
		onDragDropSuccess,
		isPeekingFromBlogs,
		isSuperAdmin,
		homepageId,
		canManageContentOrSuperAdmin,
		handleSpaceViewCleared,
	]);

	return (
		<ExpandableMenuItem isExpanded={isExpanded} onExpansionToggle={onExpansionToggle}>
			<>
				<ExpandableMenuItemTrigger
					elemBefore={
						isMultiSelectActive ? (
							<TasksIcon label="" color="currentColor" />
						) : (
							<PagesIcon label="" color="currentColor" />
						)
					}
					actionsOnHover={
						<PagesItemActions
							isLicensed={isLicensed}
							edition={edition ?? undefined}
							selection={spaceViewsSort}
							setSelection={trackedSetSpaceViewsSort}
							spaceKey={spaceKey || ''}
							expandContentSection={() => onExpansionToggle(true)}
							onItemClick={() => onExpansionToggle(true)}
							spaceHasUserContent={spaceHasUserContent}
							isPageTreeEmpty={isPageTreeEmpty}
							pageTreeFinishedLoading={pageTreeFinishedLoading}
							canManageContentOrSuperAdmin={canManageContentOrSuperAdmin}
							spaceViewsMenuTriggerRef={spaceViewsMenuTriggerRef}
						/>
					}
				>
					{isMultiSelectActive
						? formatMessage(i18n.multiSelectContentTitle)
						: formatMessage(i18n.contentTitle)}
				</ExpandableMenuItemTrigger>
			</>
			<ExpandableMenuItemContent>
				{renderContentTree}
				{/* we place the popup here and use a portal to adjust the desired location.
        This way we don't need to render a popup per pageTreeItem
        The component should only rerender whenever showHoverPageCardForItem changes.
        */}
				{isHoverPageCardOptedIn && showHoverPageCardForItem.itemID ? (
					<SpaceHoverPageCard
						showHoverPageCardForItem={showHoverPageCardForItem}
						closeAllHoverPageCards={closeAllHoverPageCards}
						clearHoverPageCardTimers={clearHoverPageCardTimers}
						startCloseHoverPageCardTimer={startCloseHoverPageCardTimer}
						onBlurHoverTarget={onBlurHoverTarget}
						shouldUseAutomationDiscovery
						key={showHoverPageCardForItem.itemID}
					/>
				) : null}
				{persistenceError && <ErrorDisplay error={persistenceError} />}
			</ExpandableMenuItemContent>
		</ExpandableMenuItem>
	);
};

const SpaceViewsControllerFromFragments: FC<SpaceViewsControllerProps> = (props) => {
	const initialPersistenceData = useFragment(
		SpaceViewsOptionsPersistenceFragment,
		props.spaceViewsPersistenceFragmentRef ?? null,
	);

	return (
		<SpaceViewsControllerComponent
			{...props}
			initialPersistenceData={initialPersistenceData ?? undefined}
		/>
	);
};

const SpaceViewsControllerDataSource: FC<SpaceViewsControllerProps> = (props) =>
	props.spaceViewsRootFragmentRef || props.spaceViewsPersistenceFragmentRef ? (
		<SpaceViewsControllerFromFragments {...props} />
	) : (
		<SpaceViewsControllerComponent {...props} />
	);

export const SpaceViewsController: FC<SpaceViewsControllerProps> = (props) => (
	<ErrorBoundary attribution={Attribution.DISCO}>
		<SpaceViewsControllerDataSource {...props} />
	</ErrorBoundary>
);
